/**
 * Low-level cryptographic primitives of the `e2e-crypto` kit.
 *
 * Everything relies on WebCrypto (`crypto.subtle`), available in browser, Bun
 * and Node ≥ 18 — no native dependencies. The only exception is the KDF
 * (Argon2id), which WebCrypto does not offer and lives in `kdf.ts` (@noble/hashes).
 *
 * AEAD: AES-256-GCM. The authentication tag (128 bits) is appended at the end
 * of the ciphertext by WebCrypto, so an envelope is `iv[12] || ct+tag`.
 * Each envelope carries **AAD** (additional authenticated data) that binds the
 * ciphertext to its context (item id, purpose, protocol version):
 * an envelope cannot be reused outside its context without failing the open.
 */

import { base64ToBytes, bytesToBase64, utf8Encode } from "./encoding";

export const AES_KEY_BYTES = 32; // AES-256
const GCM_IV_BYTES = 12;
const GCM_TAG_BITS = 128;

/** Copies to an own ArrayBuffer: avoids typing friction with BufferSource. */
function ab(bytes: Uint8Array): ArrayBuffer {
  const copy = new ArrayBuffer(bytes.length);
  new Uint8Array(copy).set(bytes);
  return copy;
}

export function randomBytes(length: number): Uint8Array {
  const bytes = new Uint8Array(length);
  crypto.getRandomValues(bytes);
  return bytes;
}

/** Random AES-256-GCM symmetric key (raw material, 32 bytes). */
export function randomSymmetricKey(): Uint8Array {
  return randomBytes(AES_KEY_BYTES);
}

export async function importAesKey(
  raw: Uint8Array,
  usages: KeyUsage[] = ["encrypt", "decrypt"],
): Promise<CryptoKey> {
  if (raw.length !== AES_KEY_BYTES) {
    throw new Error("AES key must be 32 bytes");
  }
  return crypto.subtle.importKey("raw", ab(raw), { name: "AES-GCM" }, false, usages);
}

/**
 * Seals `plaintext` with AES-256-GCM under `aad`. Returns raw bytes of the
 * envelope (`iv || ct+tag`); use {@link sealBase64} if you prefer a string.
 */
export async function aeadSeal(
  key: CryptoKey,
  plaintext: Uint8Array,
  aad: string,
): Promise<Uint8Array> {
  const iv = randomBytes(GCM_IV_BYTES);
  const ct = await crypto.subtle.encrypt(
    { name: "AES-GCM", iv: ab(iv), additionalData: ab(utf8Encode(aad)), tagLength: GCM_TAG_BITS },
    key,
    ab(plaintext),
  );
  const out = new Uint8Array(GCM_IV_BYTES + ct.byteLength);
  out.set(iv, 0);
  out.set(new Uint8Array(ct), GCM_IV_BYTES);
  return out;
}

export async function aeadOpen(
  key: CryptoKey,
  envelope: Uint8Array,
  aad: string,
): Promise<Uint8Array> {
  if (envelope.length <= GCM_IV_BYTES) {
    throw new Error("envelope too short");
  }
  const iv = envelope.subarray(0, GCM_IV_BYTES);
  const ct = envelope.subarray(GCM_IV_BYTES);
  const plaintext = await crypto.subtle.decrypt(
    { name: "AES-GCM", iv: ab(iv), additionalData: ab(utf8Encode(aad)), tagLength: GCM_TAG_BITS },
    key,
    ab(ct),
  );
  return new Uint8Array(plaintext);
}

/** Variant that seals raw material and returns base64 (for opaque blobs). */
export async function sealBase64(
  keyRaw: Uint8Array,
  plaintext: Uint8Array,
  aad: string,
): Promise<string> {
  const key = await importAesKey(keyRaw, ["encrypt"]);
  return bytesToBase64(await aeadSeal(key, plaintext, aad));
}

export async function openBase64(
  keyRaw: Uint8Array,
  envelopeB64: string,
  aad: string,
): Promise<Uint8Array> {
  const key = await importAesKey(keyRaw, ["decrypt"]);
  return aeadOpen(key, base64ToBytes(envelopeB64), aad);
}

/**
 * HKDF-Expand (RFC 5869) over `ikm` with domain label `info`. It is used
 * to derive separate sub-keys (wrap vs auth) from the same master key,
 * guaranteeing domain independence: knowing one does not reveal the other.
 */
export async function hkdfExpand(
  ikm: Uint8Array,
  info: string,
  length = AES_KEY_BYTES,
  salt: Uint8Array = new Uint8Array(0),
): Promise<Uint8Array> {
  const base = await crypto.subtle.importKey("raw", ab(ikm), "HKDF", false, ["deriveBits"]);
  const bits = await crypto.subtle.deriveBits(
    { name: "HKDF", hash: "SHA-256", salt: ab(salt), info: ab(utf8Encode(info)) },
    base,
    length * 8,
  );
  return new Uint8Array(bits);
}

export async function sha256(bytes: Uint8Array): Promise<Uint8Array> {
  return new Uint8Array(await crypto.subtle.digest("SHA-256", ab(bytes)));
}

export async function sha256Hex(bytes: Uint8Array): Promise<string> {
  const digest = await sha256(bytes);
  let hex = "";
  for (const b of digest) {
    hex += b.toString(16).padStart(2, "0");
  }
  return hex;
}

/** Generic HMAC (used by TOTP with SHA-1 and by verifiers). */
export async function hmac(
  keyRaw: Uint8Array,
  message: Uint8Array,
  hash: "SHA-1" | "SHA-256" | "SHA-512" = "SHA-256",
): Promise<Uint8Array> {
  const key = await crypto.subtle.importKey("raw", ab(keyRaw), { name: "HMAC", hash }, false, [
    "sign",
  ]);
  return new Uint8Array(await crypto.subtle.sign("HMAC", key, ab(message)));
}
